import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getToken } from '../utils/auth';
import './MCQLibraryPage.css';
import swal from 'sweetalert';

const MCQLibraryPage = () => {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // VIEW MODAL
  const [viewQuestion, setViewQuestion] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // 🔥 PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 15;

  // 🎯 SEARCH & FILTER STATE
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [favorites, setFavorites] = useState(new Set());

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const initialForm = useMemo(() => ({
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctOption: 'A',
    explanation: '',
    mark: 1,
    difficulty: 'Easy',
    module: '',
    categoryName: '',
    subcategory: ''
  }), []);

  const [formData, setFormData] = useState(initialForm);

  // 🎯 HANDLERS FOR NEW FEATURES (defined before useEffect)
  const handleCreateNew = useCallback(() => {
    setEditingId(null);
    setFormData(initialForm);
    setShowModal(true);
  }, [initialForm]);

  // 🎯 KEYBOARD SHORTCUTS
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'n') {
          e.preventDefault();
          handleCreateNew();
        }
        if (e.key === 'f') {
          e.preventDefault();
          document.querySelector('.search-input')?.focus();
        }
      }
      if (e.key === 'Escape') {
        setShowModal(false);
        setShowViewModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleCreateNew]);

  // 🎯 SEARCH & FILTER LOGIC
  const filteredAndSortedQuestions = useMemo(() => {
    let filtered = questions.filter(q => {
      const matchesSearch = q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           q.explanation.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDifficulty = !filterDifficulty || q.difficulty === filterDifficulty;
      const matchesCategory = !filterCategory || q.categoryName === filterCategory;
      return matchesSearch && matchesDifficulty && matchesCategory;
    });

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'recent') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (sortBy === 'difficulty') {
        const diffOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
        return (diffOrder[a.difficulty] || 0) - (diffOrder[b.difficulty] || 0);
      }
      if (sortBy === 'alphabetical') return a.question.localeCompare(b.question);
      return 0;
    });

    return filtered;
  }, [questions, searchTerm, filterDifficulty, filterCategory, sortBy]);

  // Fetch
  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [qRes, cRes] = await Promise.all([
        fetch(`${API_BASE_URL}/library-getAllQuestions`, { headers: { Authorization: `Bearer ${getToken()}` } }),
        fetch(`${API_BASE_URL}/api/mcq-category/getall`, { headers: { Authorization: `Bearer ${getToken()}` } })
      ]);

      const qData = await qRes.json();
      const cData = await cRes.json();

      if (qData.success) setQuestions(qData.data);
      if (cData.data) setCategories(cData.data);
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // 🔥 PAGINATION LOGIC
  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = filteredAndSortedQuestions.slice(indexOfFirstQuestion, indexOfLastQuestion);
  const totalPages = Math.ceil(filteredAndSortedQuestions.length / questionsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterDifficulty('');
    setFilterCategory('');
    setSortBy('recent');
    setCurrentPage(1);
  };

  // Input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Module
  const handleModuleChange = (e) => {
    const module = e.target.value;
    const filtered = categories.filter(c => c.module === module);
    const uniqueCategories = [...new Set(filtered.map(c => c.categoryName))];
    setFilteredCategories(uniqueCategories);
    setFilteredSubcategories([]);
    setFormData(prev => ({ ...prev, module, categoryName: '', subcategory: '' }));
  };

  // Category
  const handleCategoryChange = (e) => {
    const category = e.target.value;
    const filtered = categories.filter(
      c => c.categoryName === category && c.module === formData.module
    );
    setFilteredSubcategories(filtered.map(c => c.subcategory));
    setFormData(prev => ({ ...prev, categoryName: category, subcategory: '' }));
  };

  // EDIT
  const handleEdit = (q) => {
    setFormData(q);
    const filtered = categories.filter(c => c.module === q.module);
    setFilteredCategories([...new Set(filtered.map(c => c.categoryName))]);
    const subFiltered = categories.filter(
      c => c.categoryName === q.categoryName && c.module === q.module
    );
    setFilteredSubcategories(subFiltered.map(c => c.subcategory));
    setEditingId(q._id);
    setShowModal(true);
  };

  // SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let url = `${API_BASE_URL}/library-createQuestion`;
      let method = "POST";
      if (editingId) {
        url = `${API_BASE_URL}/library-updateQuestion/${editingId}`;
        method = "PUT";
      }
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (result.success) {
        swal("Success!", editingId ? "Question Updated" : "Question Created", "success");
        setShowModal(false);
        setEditingId(null);
        fetchInitialData();
        setFormData(initialForm);
      } else {
        swal("Error", result.message, "error");
      }
    } catch (err) {
      swal("Error", "Something went wrong", "error");
    }
  };

  // DELETE
  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This question will be permanently deleted!",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const res = await fetch(`${API_BASE_URL}/library-deleteQuestion/${id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${getToken()}` }
          });
          const result = await res.json();
          if (result.success) {
            swal("Deleted!", "Question has been deleted.", "success");
            fetchInitialData();
          } else {
            swal("Error", result.message, "error");
          }
        } catch (err) {
          swal("Error", "Failed to delete", "error");
        }
      }
    });
  };

  if (loading) return <div className="status-message">⏳ Loading...</div>;
  if (error) return <div className="status-message error-message">❌ {error}</div>;

  const uniqueCategories = [...new Set(categories.map(c => c.categoryName))];
  const isAnyFilterActive = searchTerm || filterDifficulty || filterCategory || sortBy !== 'recent';

  return (
    <div className="library-container">
      <header className="library-header">
        <div className="header-content">
          <h1>📚 MCQ Library <span className="count-badge">{questions.length}</span></h1>
          <p className="subtitle">Manage your questions with ease</p>
        </div>
        <button className="create-btn" onClick={handleCreateNew} title="Ctrl+N">
          ➕ Create Question
        </button>
      </header>

      {/* 🎯 SEARCH & FILTER BAR */}
      <div className="search-filter-bar">
        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="🔍 Search questions or explanations... (Ctrl+F)"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="filter-controls">
          <select
            className="filter-select"
            value={filterDifficulty}
            onChange={(e) => {
              setFilterDifficulty(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by difficulty"
          >
            <option value="">All Levels</option>
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
          </select>

          <select
            className="filter-select"
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by category"
          >
            <option value="">All Categories</option>
            {uniqueCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            title="Sort questions"
          >
            <option value="recent">Recently Added</option>
            <option value="alphabetical">A - Z</option>
            <option value="difficulty">Difficulty</option>
          </select>

          {isAnyFilterActive && (
            <button className="clear-filters-btn" onClick={clearAllFilters} title="Clear all filters">
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      {/* RESULTS INFO */}
      <div className="results-info">
        <span className="results-count">
          {filteredAndSortedQuestions.length === 0 
            ? 'No questions found' 
            : `${filteredAndSortedQuestions.length} question${filteredAndSortedQuestions.length !== 1 ? 's' : ''} found`}
        </span>
        {favorites.size > 0 && (
          <span className="favorites-count">⭐ {favorites.size} favorite{favorites.size !== 1 ? 's' : ''}</span>
        )}
      </div>

      {/* QUESTIONS GRID */}
      <div className="questions-grid">
        {currentQuestions.length > 0 ? (
          currentQuestions.map((q) => (
            <div 
              key={q._id} 
              className={`question-card ${favorites.has(q._id) ? 'favorite' : ''}`}
            >
              {/* CARD HEADER */}
              <div className="card-header">
                <div className="badge-row">
                  <span className="category-badge" title={`${q.module} → ${q.categoryName}`}>
                    {q.module} → {q.categoryName}
                  </span>
                  <span className={`difficulty-badge difficulty-${q.difficulty.toLowerCase()}`}>
                    {q.difficulty}
                  </span>
                </div>

                <div className="card-top-actions">
                  <button
                    className={`favorite-btn ${favorites.has(q._id) ? 'favorited' : ''}`}
                    onClick={() => toggleFavorite(q._id)}
                    title="Add to favorites"
                  >
                    {favorites.has(q._id) ? '⭐' : '☆'}
                  </button>
                </div>
              </div>

              {/* QUESTION TEXT */}
              <h3 className="question-text" title={q.question}>
                {q.question.length > 100 ? q.question.substring(0, 100) + '...' : q.question}
              </h3>

              {/* OPTIONS GRID */}
              <div className="options-grid">
                {['A', 'B', 'C', 'D'].map(opt => (
                  <div 
                    key={opt}
                    className={`option-item ${q.correctOption === opt ? 'correct-preview' : ''}`}
                    title={q[`option${opt}`]}
                  >
                    <span className="opt-label">{opt}:</span>
                    <span className="opt-text">
                      {q[`option${opt}`].length > 40 ? q[`option${opt}`].substring(0, 40) + '...' : q[`option${opt}`]}
                    </span>
                  </div>
                ))}
              </div>

              {/* MARKS INFO */}
              <div className="marks-info">
                <span>📍 Marks: <strong>{q.mark}</strong></span>
              </div>

              {/* CARD ACTIONS */}
              <div className="card-actions">
                <button 
                  className="view-btn" 
                  onClick={() => { 
                    setViewQuestion(q); 
                    setShowViewModal(true); 
                  }}
                  title="View full details"
                >
                  👁 View
                </button>
                <button 
                  className="edit-btn" 
                  onClick={() => handleEdit(q)}
                  title="Edit question"
                >
                  ✏️ Edit
                </button>
                <button 
                  className="delete-btn" 
                  onClick={() => handleDelete(q._id)}
                  title="Delete question"
                >
                  🗑 Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-results">
            <p>🔍 No questions match your filters</p>
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              Try clearing filters
            </button>
          </div>
        )}
      </div>

      {/* 🔥 PAGINATION CONTROLS */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button 
            disabled={currentPage === 1} 
            onClick={() => paginate(currentPage - 1)}
            className="pag-btn"
            title="Previous page"
          >
            ← Prev
          </button>

          <button 
            disabled={currentPage === totalPages} 
            onClick={() => paginate(currentPage + 1)}
            className="pag-btn"
            title="Next page"
          >
            Next →
          </button>
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingId ? "Update Question" : "Add New Question"}</h2>
            <form onSubmit={handleSubmit} className="question-form">
              <select value={formData.module} onChange={handleModuleChange} required>
                <option value="">Select Module</option>
                {[...new Set(categories.map(c => c.module))].map((mod) => (
                  <option key={mod}>{mod}</option>
                ))}
              </select>

              <select value={formData.categoryName} onChange={handleCategoryChange} required>
                <option value="">Select Category</option>
                {filteredCategories.map(cat => <option key={cat}>{cat}</option>)}
              </select>

              <select value={formData.subcategory} onChange={(e)=>setFormData({...formData,subcategory:e.target.value})} required>
                <option value="">Select Subcategory</option>
                {filteredSubcategories.map(sub => <option key={sub}>{sub}</option>)}
              </select>

              <textarea name="question" value={formData.question} onChange={handleInputChange} required placeholder="Question text..."/>

              <div className="form-grid">
                <input name="optionA" value={formData.optionA} onChange={handleInputChange} required placeholder="Option A"/>
                <input name="optionB" value={formData.optionB} onChange={handleInputChange} required placeholder="Option B"/>
                <input name="optionC" value={formData.optionC} onChange={handleInputChange} required placeholder="Option C"/>
                <input name="optionD" value={formData.optionD} onChange={handleInputChange} required placeholder="Option D"/>
              </div>

              <div className="form-grid">
                <select name="correctOption" value={formData.correctOption} onChange={handleInputChange}>
                  <option>A</option><option>B</option><option>C</option><option>D</option>
                </select>
                <select name="difficulty" value={formData.difficulty} onChange={handleInputChange}>
                  <option>Easy</option><option>Medium</option><option>Hard</option>
                </select>
                <input type="number" name="mark" value={formData.mark} onChange={handleInputChange}/>
              </div>

              <textarea name="explanation" value={formData.explanation} onChange={handleInputChange} placeholder="Explanation..."/>

              <div className="modal-actions">
                <button type="button" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit">{editingId ? "Update" : "Save"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {showViewModal && viewQuestion && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content detail-modal modern" onClick={(e) => e.stopPropagation()}>
            <div className="detail-header modern">
              <div className="header-info">
                <h2>📋 Question Details</h2>
                <div className="header-badges">
                  <span className={`difficulty-pill ${viewQuestion.difficulty.toLowerCase()}`}>
                    {viewQuestion.difficulty} • {viewQuestion.mark} marks
                  </span>
                  <span className="category-pill">{viewQuestion.categoryName}</span>
                </div>
              </div>
              <button className="close-btn" onClick={() => setShowViewModal(false)} title="Close (Esc)">✕</button>
            </div>

            <div className="detail-content modern">
              <div className="detail-card">
                <h3>❓ Question</h3>
                <p className="full-text">{viewQuestion.question}</p>
              </div>

              <div className="detail-card">
                <h3>🎯 Options</h3>
                <div className="options-detail modern">
                  {['A','B','C','D'].map(opt => (
                    <div 
                      key={opt} 
                      className={`option-detail modern ${viewQuestion.correctOption === opt ? 'correct' : ''}`}
                    >
                      <span className={`option-label ${viewQuestion.correctOption === opt ? 'correct-label' : ''}`}>
                        {opt} {viewQuestion.correctOption === opt && '✓'}
                      </span>
                      <span className="option-text">{viewQuestion[`option${opt}`]}</span>
                    </div>
                  ))}
                </div>
              </div>

              {viewQuestion.explanation && (
                <div className="detail-card">
                  <h3>💡 Explanation</h3>
                  <p className="full-text explanation-text">{viewQuestion.explanation}</p>
                </div>
              )}

              <div className="detail-card meta-info">
                <div className="meta-item">
                  <span className="meta-label">Module:</span>
                  <span className="meta-value">{viewQuestion.module}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Subcategory:</span>
                  <span className="meta-value">{viewQuestion.subcategory || 'N/A'}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Difficulty:</span>
                  <span className={`meta-value difficulty-${viewQuestion.difficulty.toLowerCase()}`}>{viewQuestion.difficulty}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Marks:</span>
                  <span className="meta-value">{viewQuestion.mark}</span>
                </div>
              </div>
            </div>

            <div className="detail-actions">
              <button className="modal-action-btn edit" onClick={() => { handleEdit(viewQuestion); setShowViewModal(false); }}>
                ✏️ Edit
              </button>
              <button className="modal-action-btn delete" onClick={() => { handleDelete(viewQuestion._id); setShowViewModal(false); }}>
                🗑 Delete
              </button>
              <button className="modal-action-btn close" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCQLibraryPage;